import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { corpoSlice } from "../../../../../features/business/scorpSlice";
import { useDispatch } from "react-redux";
import ProtectedPaymentRoute from "../../../../auth/Auth";

export default function CorporationType() {
  ProtectedPaymentRoute();

  const [selectedType, setSelectedType] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const actions = corpoSlice.actions;

  const sCorpPoints = [
    {
      title: "Formation",
      desc: "Formed by filing Articles of Incorporation with the state, then electing S-Corp tax status with the IRS under Subchapter S.",
    },
    {
      title: "Taxation",
      desc: "Avoids corporate double taxation. Profits and losses pass through directly to personal tax returns at individual tax rates.",
    },
    {
      title: "Ownership",
      desc: "Limited to 100 shareholders (all must be U.S. citizens or residents) with one class of stock permitted.",
    },
    {
      title: "Flexibility",
      desc: "Provides strong personal liability protection with optimal pass-through tax savings for operating businesses.",
    },
    {
      title: "Regulations",
      desc: "Fewer regulatory formalities compared to C-Corps while maintaining IRS qualification criteria.",
    },
  ];

  const cCorpPoints = [
    {
      title: "Formation",
      desc: "Requires filing Articles of Incorporation with the state, creating corporate bylaws, and holding initial organizational board meetings.",
    },
    {
      title: "Taxation",
      desc: "Taxed as an independent legal entity on corporate profits, with shareholders taxed separately on dividends received.",
    },
    {
      title: "Ownership",
      desc: "Unlimited number of shareholders worldwide (including foreign investors) with multiple classes of stock (Common & Preferred).",
    },
    {
      title: "Flexibility",
      desc: "The gold standard for startups seeking venture capital funding, issuing employee stock options (ESOP), or planning an IPO.",
    },
    {
      title: "Regulations",
      desc: "Requires formal corporate governance, annual shareholder meetings, board minutes, and detailed state reporting.",
    },
  ];

  const keyConsiderations = [
    {
      title: "Tax Benefits",
      desc: "S-Corps offer significant tax savings through pass-through income, while C-Corps allow corporate profit retention at flat corporate rates.",
      icon: "fa-solid fa-percent",
    },
    {
      title: "Growth & Investment",
      desc: "C-Corps are universally preferred by venture capitalists and angel investors due to unlimited shareholders and preferred stock options.",
      icon: "fa-solid fa-chart-line",
    },
    {
      title: "Operational Complexity",
      desc: "Consider your compliance capacity. S-Corps are simpler for closely held companies, whereas C-Corps require structured corporate record-keeping.",
      icon: "fa-solid fa-scale-balanced",
    },
  ];

  const handleSelect = (type, route = "/business_name") => {
    setSelectedType(type);
    dispatch(actions.update_corpo(type));
    navigate(route);
  };

  return (
    <div className="min-h-[85vh] bg-[#f8f9fa] py-8 lg:py-14 font-sans">
      <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
        
        {/* Navigation & Step Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#dadce0]">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-[4px] border border-[#dadce0] bg-white text-[#1a73e8] text-[13.5px] font-medium hover:bg-[#f1f3f4] transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-arrow-left text-xs"></i> Back
          </button>
          <span className="text-xs font-semibold text-[#5f6368] tracking-wider uppercase">
            Step 1 of 8: Corporation Formation • Entity Selection
          </span>
        </div>

        {/* Hero Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="text-[#1a73e8] font-semibold tracking-wider uppercase text-xs mb-2 block">
            Corporation Entity Structure
          </span>
          <h1 className="text-3xl sm:text-4xl font-normal text-[#202124] tracking-tight mb-3">
            Select your corporation type
          </h1>
          <p className="text-[15px] text-[#5f6368] leading-relaxed">
            Understand the tax advantages, ownership structures, and growth models of <span className="font-medium text-[#202124]">S-Corporations</span> and <span className="font-medium text-[#202124]">C-Corporations</span> before proceeding.
          </p>
        </div>

        {/* 2 Main Choice Cards (S-Corp vs C-Corp) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10 items-stretch">
          
          {/* Card 1: S-Corporation */}
          <div
            onClick={() => setSelectedType("S-Corp")}
            className={`bg-white rounded-[16px] border p-8 shadow-sm transition-all duration-200 flex flex-col justify-between cursor-pointer hover:shadow-md ${
              selectedType === "S-Corp"
                ? "border-[#1a73e8] ring-2 ring-[#1a73e8]/20 bg-[#f8fafd]"
                : "border-[#dadce0] hover:border-[#1a73e8]/60"
            }`}
          >
            <div>
              {/* Card Top */}
              <div className="flex items-center justify-between pb-5 mb-5 border-b border-[#f1f3f4]">
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-xl bg-[#e8f0fe] text-[#1a73e8] flex items-center justify-center text-xl shadow-xs">
                    <i className="fa-solid fa-file-invoice-dollar"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-medium text-[#202124]">S-Corporation</h2>
                    <span className="text-xs text-[#5f6368]">Pass-through tax classification</span>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#e8f0fe] text-[#1a73e8]">
                  Tax Savings
                </span>
              </div>

              <p className="text-[14.5px] text-[#3c4043] leading-relaxed mb-6 font-normal">
                An S corporation functions as a pass-through entity, so income is passed to shareholders and federal corporate income taxes are typically avoided.
              </p>

              {/* Point-by-Point Breakdown */}
              <div className="space-y-4 mb-8">
                {sCorpPoints.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-[13.5px]">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#e8f0fe] text-[#1a73e8] flex items-center justify-center mt-0.5 text-xs">
                      <i className="fa-solid fa-check"></i>
                    </div>
                    <div>
                      <span className="font-semibold text-[#202124] mr-1">{item.title}:</span>
                      <span className="text-[#5f6368] leading-relaxed">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSelect("S-Corp");
              }}
              className="w-full py-3.5 px-6 rounded-[6px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium text-[15px] shadow-sm transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Continue as S-Corp</span>
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </button>
          </div>

          {/* Card 2: C-Corporation */}
          <div
            onClick={() => setSelectedType("C-Corp")}
            className={`bg-white rounded-[16px] border p-8 shadow-sm transition-all duration-200 flex flex-col justify-between cursor-pointer hover:shadow-md ${
              selectedType === "C-Corp"
                ? "border-[#1a73e8] ring-2 ring-[#1a73e8]/20 bg-[#f8fafd]"
                : "border-[#dadce0] hover:border-[#1a73e8]/60"
            }`}
          >
            <div>
              {/* Card Top */}
              <div className="flex items-center justify-between pb-5 mb-5 border-b border-[#f1f3f4]">
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-xl bg-[#e6f4ea] text-[#137333] flex items-center justify-center text-xl shadow-xs">
                    <i className="fa-solid fa-building-columns"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-medium text-[#202124]">C-Corporation</h2>
                    <span className="text-xs text-[#5f6368]">Standard corporate entity structure</span>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#e6f4ea] text-[#137333]">
                  Venture Ready
                </span>
              </div>

              <p className="text-[14.5px] text-[#3c4043] leading-relaxed mb-6 font-normal">
                A C corporation is a separate legal entity where owners are taxed separately from the corporation itself. Ideal for raising venture capital and issuing stock.
              </p>

              {/* Point-by-Point Breakdown */}
              <div className="space-y-4 mb-8">
                {cCorpPoints.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-[13.5px]">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#e6f4ea] text-[#137333] flex items-center justify-center mt-0.5 text-xs">
                      <i className="fa-solid fa-check"></i>
                    </div>
                    <div>
                      <span className="font-semibold text-[#202124] mr-1">{item.title}:</span>
                      <span className="text-[#5f6368] leading-relaxed">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSelect("C-Corp");
              }}
              className="w-full py-3.5 px-6 rounded-[6px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium text-[15px] shadow-sm transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Continue as C-Corp</span>
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </button>
          </div>

        </div>

        {/* 3rd Row: Key Decision Considerations */}
        <div className="bg-white rounded-[14px] border border-[#dadce0] p-8 shadow-sm mb-10">
          <h3 className="text-lg font-medium text-[#202124] mb-6 pb-3 border-b border-[#f1f3f4] flex items-center gap-2.5">
            <i className="fa-solid fa-lightbulb text-[#fbbc04]"></i>
            Key Considerations Before Choosing
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {keyConsiderations.map((c, idx) => (
              <div key={idx} className="p-5 rounded-[10px] bg-[#f8f9fa] border border-[#dadce0] flex flex-col">
                <div className="w-9 h-9 rounded-lg bg-white border border-[#dadce0] text-[#1a73e8] flex items-center justify-center text-sm mb-3.5 shadow-2xs">
                  <i className={c.icon}></i>
                </div>
                <h4 className="font-semibold text-[#202124] text-[14.5px] mb-1.5">{c.title}</h4>
                <p className="text-[#5f6368] text-[13px] leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Trust & Guarantee Bar */}
        <div className="p-4 rounded-[10px] bg-[#f8fafd] border border-[#dadce0] flex flex-wrap items-center justify-center gap-8 text-xs text-[#5f6368]">
          <span className="flex items-center gap-2">
            <i className="fa-solid fa-shield-halved text-[#1e8e3e] text-sm"></i> 256-Bit SSL Encrypted
          </span>
          <span className="flex items-center gap-2">
            <i className="fa-solid fa-circle-check text-[#1a73e8] text-sm"></i> 100% State Guarantee
          </span>
          <span className="flex items-center gap-2">
            <i className="fa-solid fa-arrows-rotate text-[#1a73e8] text-sm"></i> Elect S-Corp Status Anytime via Form 2553
          </span>
        </div>

      </div>
    </div>
  );
}
